import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { AvatarUpload } from "@libs/zapi-ui/ds/components/avatar-upload";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Textarea } from "@libs/zapi-ui/ds/components/textarea";
import { BuildingsIcon, CpuIcon } from "@phosphor-icons/react";
import { useState, useEffect, useId, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE } from "../const/const";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUpdateOrganization } from "../services/useUpdateOrganization";
import { useDeleteOrganizationLogo } from "../services/useDeleteOrganizationLogo";
import { useAiModelSettings } from "../services/useAiModelSettings";
import { useOrganization } from "../services/useOrganization";
import {
  SettingsError,
  SettingsHeader,
  SettingsRow,
  SettingsSection,
  SettingsSectionActions,
  SettingsSectionSkeleton,
  SettingsValue
} from "./blocks/SettingsLayout";

type TOrganizationData = {
  organizationName: string;
  customerInstructions: string;
  organizationLogoUrl: string | null;
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");

function AIModelRow() {
  const { t } = useTranslation("settings");
  const { aiModelSettings, isLoading } = useAiModelSettings();

  if (!isLoading && !aiModelSettings) return null;

  return (
    <SettingsRow label={t("redesign.organization.aiModel")} description={t("redesign.organization.aiModelHint")}>
      {isLoading || !aiModelSettings ? (
        <Skeleton className="h-4 w-32" />
      ) : (
        <SettingsValue icon={CpuIcon}>
          {aiModelSettings.isUsingOrganizationOverride
            ? aiModelSettings.currentModel
            : t("redesign.organization.defaultModel")}
        </SettingsValue>
      )}
    </SettingsRow>
  );
}

export function GeneralSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { organizationId: userOrganizationId, isLabs } = useMeStore();
  const { currentOrganizationId, updateOrganizationName } = useOrganizationStore();
  const {
    data: organizationData,
    refetch: refetchOrganization,
    isLoading: isLoadingOrganization,
    isError: isOrganizationError
  } = useOrganization();
  const queryClient = useQueryClient();
  const formId = useId();
  const orgNameId = useId();
  const instructionsId = useId();

  // Use currentOrganizationId if available (for superAdmin), otherwise fall back to user's organizationId
  const organizationId = currentOrganizationId || userOrganizationId;

  const { mutate: updateOrganization, isPending: isSaving } = useUpdateOrganization();
  const { mutate: deleteOrganizationLogo, isPending: isDeleting } = useDeleteOrganizationLogo();

  const [orgName, setOrgName] = useState("");
  const [customerInstructionsValue, setCustomerInstructionsValue] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (organizationData) {
      setOrgName(organizationData.organizationName ?? "");
    }
  }, [organizationData]);

  useEffect(() => {
    if (organizationData) {
      setCustomerInstructionsValue(organizationData.customerInstructions);
    }
  }, [organizationData]);

  const savedOrgName = organizationData?.organizationName ?? "";
  const savedInstructions = organizationData?.customerInstructions ?? "";
  const isChanged =
    orgName !== savedOrgName || customerInstructionsValue !== savedInstructions || selectedFile !== null;
  const isBusy = isSaving || isDeleting;

  function validateFile(file: File): string | null {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      toast.error(t("general.invalidFileType"));
      return t("general.invalidFileType");
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("general.fileTooLarge"));
      return t("general.fileTooLarge");
    }

    return null;
  }

  function fileSelectHandler(file: File) {
    setUploadError(null);

    const validationError = validateFile(file);

    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function clearSelectedFile() {
    setSelectedFile(null);
    setPreviewUrl(null);
  }

  function handleDiscard() {
    setOrgName(savedOrgName);
    setCustomerInstructionsValue(savedInstructions);
    clearSelectedFile();
    setUploadError(null);
  }

  const handleRemovePhoto = async () => {
    if (!organizationId) {
      toast.error(t("general.orgIdMissing"));
      return;
    }

    setUploadError(null);

    deleteOrganizationLogo(
      { organizationId },
      {
        onSuccess: async () => {
          // Optimistically update query cache to remove logo URL
          queryClient.setQueryData<TOrganizationData>(["organization", currentOrganizationId], (old) => {
            if (!old) return old;
            return { ...old, organizationLogoUrl: null };
          });

          toast.success(t("general.logoRemoved"));
          await refetchOrganization();
        },
        onError: (error) => {
          console.error("Failed to remove organization logo:", error);
          const err = error as { response?: { data?: { message?: string } } };
          setUploadError(err.response?.data?.message || t("general.logoRemoveFailed"));
          toast.error(t("general.logoRemoveFailed"));
        }
      }
    );
  };

  const handleSaveChanges = async (event: FormEvent) => {
    event.preventDefault();
    if (!organizationId) {
      toast.error(t("general.orgIdMissing"));
      return;
    }

    setUploadError(null);

    updateOrganization(
      {
        organizationId,
        name: orgName,
        file: selectedFile ?? undefined,
        customerInstructions: customerInstructionsValue
      },
      {
        onSuccess: async () => {
          // Optimistically update query cache immediately to reset isChanged
          // Note: Logo URL will be updated after refetch, but we update name and instructions immediately
          queryClient.setQueryData<TOrganizationData>(["organization", currentOrganizationId], (old) => {
            if (!old) return old;
            return {
              ...old,
              organizationName: orgName,
              customerInstructions: customerInstructionsValue
            };
          });

          // Update organization name in the organization store (for the dropdown)
          if (organizationId) {
            updateOrganizationName(organizationId, orgName);
          }

          // Update local state immediately with saved values
          setOrgName(orgName);
          setCustomerInstructionsValue(customerInstructionsValue);

          toast.success(t("general.updated"));
          await refetchOrganization();

          clearSelectedFile();
        },
        onError: (error) => {
          toast.error(t("general.updateFailed"));
          console.error("Failed to update organization:", error);
        }
      }
    );
  };

  const header = (actions?: ReactNode) => <SettingsHeader title={t("tabs.general")} actions={actions} />;

  if (isLoadingOrganization) {
    return (
      <>
        {header()}
        <SettingsSectionSkeleton media rows={2} />
      </>
    );
  }

  if (isOrganizationError || !organizationData) {
    return (
      <>
        {header()}
        <SettingsError
          title={t("redesign.loadFailed")}
          description={t("redesign.loadFailedHint")}
          retryLabel={t("redesign.retry")}
          onRetry={() => void refetchOrganization()}
        />
      </>
    );
  }

  const organizationLogoUrl = organizationData.organizationLogoUrl || null;

  return (
    <>
      {header(
        <SettingsSectionActions status={isChanged ? t("redesign.unsaved") : undefined}>
          {isChanged && (
            <Button type="button" variant="ghost" shape="pill" disabled={isBusy} onClick={handleDiscard}>
              {t("redesign.discard")}
            </Button>
          )}
          <Button type="submit" form={formId} shape="pill" data-testid="general-settings-save-button" disabled={isBusy}>
            {isSaving && <Spinner data-icon="inline-start" aria-label={t("common:loading")} />}
            {t("common.save")}
          </Button>
        </SettingsSectionActions>
      )}
      <SettingsSection title={t("redesign.organization.title")}>
        <AvatarUpload
          tone="secondary"
          src={previewUrl ?? organizationLogoUrl}
          fallback={initialsOf(savedOrgName) || <BuildingsIcon className="size-8" />}
          uploadLabel={t("redesign.organization.uploadLogo")}
          removeLabel={t("redesign.organization.removeLogo")}
          removingLabel={t("general.removing")}
          hint={t("redesign.photoHint")}
          error={uploadError}
          pending={selectedFile !== null}
          removing={isDeleting}
          disabled={isSaving}
          onSelect={fileSelectHandler}
          onRemove={organizationLogoUrl ? handleRemovePhoto : undefined}
        />
        <form id={formId} onSubmit={handleSaveChanges}>
          <FieldGroup className="gap-5">
            <Field>
              <FieldLabel htmlFor={orgNameId}>{t("redesign.organization.name")}</FieldLabel>
              <Input
                id={orgNameId}
                autoComplete="organization"
                data-testid="general-settings-organization-name-input"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                disabled={isBusy}
              />
            </Field>
            {isLabs && (
              <Field>
                <FieldLabel htmlFor={instructionsId}>{t("redesign.organization.instructions")}</FieldLabel>
                <Textarea
                  id={instructionsId}
                  data-testid="general-settings-organization-instructions-input"
                  value={customerInstructionsValue}
                  onChange={(e) => setCustomerInstructionsValue(e.target.value)}
                  disabled={isBusy}
                  rows={6}
                  placeholder={t("general.orgInstructionsPlaceholder")}
                />
                <FieldDescription>{t("redesign.organization.instructionsHint")}</FieldDescription>
              </Field>
            )}
          </FieldGroup>
        </form>
        {!isLabs && <AIModelRow />}
      </SettingsSection>
    </>
  );
}
